type ObserveIntersectionOptions = {
  root?: Element | null;
  rootMargin?: string;
  threshold?: number;
};

export var LAYOUT_CHANGE_EVENT = "wonmov:layout-change";

function parseRootMargin(value: string | undefined): number {
  if (!value) {
    return 0;
  }
  var match = value.match(/(-?\d+)/);
  if (!match) {
    return 0;
  }
  return parseInt(match[1], 10);
}

function getElementRect(element: HTMLElement): {
  top: number;
  left: number;
  bottom: number;
  right: number;
  width: number;
  height: number;
} {
  var rect = element.getBoundingClientRect();

  if (rect.width > 0 || rect.height > 0) {
    return {
      top: rect.top,
      left: rect.left,
      bottom: rect.bottom,
      right: rect.right,
      width: rect.width,
      height: rect.height,
    };
  }

  if (element.offsetWidth > 0 || element.offsetHeight > 0) {
    var top = 0;
    var left = 0;
    var node: HTMLElement | null = element;

    while (node) {
      top += node.offsetTop;
      left += node.offsetLeft;
      node = node.offsetParent as HTMLElement | null;
    }

    var width = element.offsetWidth;
    var height = element.offsetHeight;

    return {
      top: top,
      left: left,
      bottom: top + height,
      right: left + width,
      width: width,
      height: height,
    };
  }

  return {
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    width: 0,
    height: 0,
  };
}

function rectUsesDocumentCoords(rect: { top: number; left: number }): boolean {
  return rect.top > window.innerHeight * 2 || rect.left > window.innerWidth * 2;
}

export function isElementInViewport(
  element: HTMLElement,
  margin: number,
  root?: Element | null
): boolean {
  var rect = getElementRect(element);
  var viewTop = 0;
  var viewBottom = window.innerHeight || document.documentElement.clientHeight;
  var viewLeft = 0;
  var viewRight = window.innerWidth || document.documentElement.clientWidth;

  if (root) {
    var rootRect = root.getBoundingClientRect();
    viewTop = rootRect.top;
    viewBottom = rootRect.bottom;
    viewLeft = rootRect.left;
    viewRight = rootRect.right;
  }

  if (rect.width === 0 && rect.height === 0) {
    return false;
  }

  if (rectUsesDocumentCoords(rect)) {
    var scrollTop =
      window.pageYOffset ||
      (document.documentElement && document.documentElement.scrollTop) ||
      0;
    var scrollLeft =
      window.pageXOffset ||
      (document.documentElement && document.documentElement.scrollLeft) ||
      0;
    viewTop = scrollTop - margin;
    viewBottom = scrollTop + (window.innerHeight || document.documentElement.clientHeight) + margin;
    viewLeft = scrollLeft - margin;
    viewRight = scrollLeft + (window.innerWidth || document.documentElement.clientWidth) + margin;

    return (
      rect.bottom >= viewTop &&
      rect.top <= viewBottom &&
      rect.right >= viewLeft &&
      rect.left <= viewRight
    );
  }

  return (
    rect.bottom >= viewTop - margin &&
    rect.top <= viewBottom + margin &&
    rect.right >= viewLeft - margin &&
    rect.left <= viewRight + margin
  );
}

function dispatchLayoutChangeEvent() {
  if (typeof window === "undefined" || !window.dispatchEvent) {
    return;
  }

  try {
    if (typeof Event === "function") {
      window.dispatchEvent(new Event(LAYOUT_CHANGE_EVENT));
      return;
    }
  } catch (e) {
    // fall through
  }

  try {
    var legacyEvent = document.createEvent("Event");
    legacyEvent.initEvent(LAYOUT_CHANGE_EVENT, true, true);
    window.dispatchEvent(legacyEvent);
  } catch (e2) {
    // ignore
  }
}

export function notifyLayoutChange(): void {
  dispatchLayoutChangeEvent();
}

function createIntersectionObserver(
  element: HTMLElement,
  callback: (isIntersecting: boolean) => void,
  options?: ObserveIntersectionOptions
): (() => void) | null {
  if (typeof IntersectionObserver === "undefined") {
    return null;
  }

  try {
    var observer = new IntersectionObserver(
      function (entries) {
        var entry = entries[0];
        if (entry) {
          callback(entry.isIntersecting);
        }
      },
      {
        root: options && options.root ? options.root : null,
        rootMargin: options && options.rootMargin ? options.rootMargin : "0px",
        threshold: options && options.threshold !== undefined ? options.threshold : 0,
      }
    );
    observer.observe(element);
    return function () {
      observer.disconnect();
    };
  } catch (e) {
    return null;
  }
}

export function observeElementIntersection(
  element: HTMLElement,
  callback: (isIntersecting: boolean) => void,
  options?: ObserveIntersectionOptions
): () => void {
  var root = options && options.root ? options.root : null;
  var margin = parseRootMargin(options && options.rootMargin ? options.rootMargin : undefined);
  var wasVisible = false;
  var pollTimer = 0;
  var disconnectObserver: (() => void) | null = null;

  function check() {
    var visible = isElementInViewport(element, margin, root);
    if (visible) {
      callback(true);
      wasVisible = true;
      return;
    }
    if (wasVisible) {
      wasVisible = false;
      callback(false);
    }
  }

  var onActivity = function () {
    check();
  };

  disconnectObserver = createIntersectionObserver(element, function (isIntersecting) {
    if (isIntersecting) {
      callback(true);
      wasVisible = true;
      return;
    }
    if (wasVisible) {
      wasVisible = false;
      callback(false);
    }
  }, options);

  check();
  pollTimer = window.setInterval(check, 300);

  if (root && root.addEventListener) {
    root.addEventListener("scroll", onActivity);
  }
  if (window.addEventListener) {
    window.addEventListener("scroll", onActivity);
    window.addEventListener("resize", onActivity);
    window.addEventListener("touchmove", onActivity);
    window.addEventListener("mouseup", onActivity);
    window.addEventListener(LAYOUT_CHANGE_EVENT, onActivity);
  }

  return function () {
    if (pollTimer) {
      window.clearInterval(pollTimer);
    }
    if (disconnectObserver) {
      disconnectObserver();
    }
    if (root && root.removeEventListener) {
      root.removeEventListener("scroll", onActivity);
    }
    if (window.removeEventListener) {
      window.removeEventListener("scroll", onActivity);
      window.removeEventListener("resize", onActivity);
      window.removeEventListener("touchmove", onActivity);
      window.removeEventListener("mouseup", onActivity);
      window.removeEventListener(LAYOUT_CHANGE_EVENT, onActivity);
    }
  };
}
